import React, { useMemo, useRef, useState } from 'react';
import { BsChevronDown, BsXLg } from 'react-icons/bs';
import { Box, Button, Flex, HStack, Icon, IconButton, Popover, PopoverBody, PopoverContent, PopoverHeader, PopoverTrigger, Text, useDisclosure } from '@chakra-ui/react';
import { useSelect } from 'downshift';
import PropTypes from 'prop-types';

import { menuItemBaseStyle } from '../../styles/theme/components/menu';
import colors from '../../styles/theme/foundation/colors';
import { activeStyle, hoverStyle, popoverMenuButtonStyles } from '../../styles/theme/util/popoverMenuButtonStyles';
import SearchInputGroup from '../SearchInputGroup';
import LoadingSpinner from '../Spinner';
import TruncationTooltip from '../TruncationTooltip';

const RQSearchableDropdownMenu = ({
  reactQuery,
  isDisabled,
  selected,
  onSelect,
  onCancel,
  searchKey,
  getLabel,
  idKey,
  width = '300px',
  placeholder = 'None Selected',
  dataAnalyticsKey = undefined,
  duskSelector = undefined
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const initRef = useRef();

  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading } = reactQuery;

  const items = useMemo(() => {
    if (isLoading) {
      return [];
    }

    const searchValue = searchTerm.toLowerCase();

    if (Array.isArray(data)) {
      return data.filter(i => i[searchKey].toLowerCase().includes(searchValue));
    }

    return [];
  }, [data, isLoading, searchTerm]);

  const getButtonStyle = () => {
    let style = { ...popoverMenuButtonStyles, width, color: colors.greyScale['500'] };
    if (selected) style = { ...popoverMenuButtonStyles, color: colors.greyScale['900'], width };
    if (isOpen) {
      style = {
        ...popoverMenuButtonStyles,
        width,
        color: colors.primaryScale['600'],
        borderColor: colors.primaryScale['600'],
        boxShadow: `inset 0 0 0 1px ${colors.primaryScale['600']}`
      };
    }

    return style;
  };

  const {
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    reset
  } = useSelect({
    items,
    onSelectedItemChange: ({ selectedItem }) => {
      onSelect(selectedItem);
      onClose();
      setSearchTerm('');
    }
  });

  return (
    <Popover
      closeOnBlur
      initialFocusRef={initRef}
      isOpen={isOpen}
      returnFocusOnClose={false}
      onClose={onClose}
      onOpen={onOpen}
    >
      <PopoverTrigger>
        <Button
          _active={activeStyle}
          _hover={hoverStyle}
          data-analytics-id={dataAnalyticsKey}
          dusk={duskSelector}
          isDisabled={isDisabled}
          style={getButtonStyle()}
          variant="unstyled"
          width={width}
          {...getToggleButtonProps()}
        >
          <Flex
            alignItems="center"
            justify="space-between"
            pointerEvents="none"
            width="100%"
          >
            <TruncationTooltip label={selected ? getLabel(selected) : placeholder}>
              <Text noOfLines={1} pointerEvents="auto" width="90%">
                {selected ? getLabel(selected) : placeholder}
              </Text>
            </TruncationTooltip>
            <Icon as={BsChevronDown} color={isOpen ? 'inherit' : colors.greyScale['500']} />
          </Flex>
        </Button>
      </PopoverTrigger>
      <PopoverContent maxH={350} width={width}>
        <PopoverHeader as={Flex} justifyContent="space-between" p={2} width={width}>
          <HStack width={width}>
            <SearchInputGroup
              dataAnalyticsId={`${dataAnalyticsKey}-search-input`}
              isDisabled={isLoading}
              searchTerm={searchTerm}
              variant="primary"
              width="100%"
              onChange={e => {
                setSearchTerm(e.target.value);
              }}
            />
            <IconButton
              aria-label="Cancel Input"
              icon={<Icon as={BsXLg} />}
              size="sm"
              variant="ghost"
              width="100px"
              onClick={() => {
                reset();
                setSearchTerm('');
                onSelect(null);
                onClose();
              }}
            />
          </HStack>
        </PopoverHeader>
        <PopoverBody width={width}>
          <ul {...getMenuProps()} style={{ height: items.length < 6 ? 'auto' : 225, overflow: 'auto' }}>
            {isLoading
              ? (
                <Box height="100px" my={4}>
                  <LoadingSpinner minHeight="100px" />
                </Box>
              ) : (
                <>
                  {items.length
                    ? items.map((item, index) => (
                      <TruncationTooltip
                        key={item[idKey]}
                        label={getLabel(item)}
                      >
                        <Button
                          _hover={{
                            cursor: 'pointer',
                            bgColor: colors.primaryScale['50'],
                            color: colors.primaryScale['600']
                          }}
                          _selected={menuItemBaseStyle._focus} //eslint-disable-line
                          borderRadius="md"
                          color={colors.greyScale['600']}
                          data-analytics-id={item?.dataAnalyticsId}
                          dusk={`${duskSelector}-item_id-${item.value}`}
                          height="40px"
                          id={item[idKey]}
                          noOfLines={1}
                          padding="10px 14px"
                          size="md"
                          textAlign="left"
                          textStyle="bodyMd500"
                          value={item[idKey]}
                          variant="unstyled"
                          width="100%"
                          {...getItemProps({ item, index })}
                        >
                          {getLabel(item)}
                        </Button>
                      </TruncationTooltip>
                    )) : <Text color={colors.greyScale['500']} textStyle="bodyMd">No results</Text>}
                </>
              )}
          </ul>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

RQSearchableDropdownMenu.propTypes = {
  reactQuery: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool,
  selected: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  searchKey: PropTypes.string.isRequired,
  getLabel: PropTypes.func.isRequired,
  idKey: PropTypes.string.isRequired,
  width: PropTypes.string,
  placeholder: PropTypes.string,
  dataAnalyticsKey: PropTypes.string,
  duskSelector: PropTypes.string
};

export default RQSearchableDropdownMenu;
